import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, inputCls, labelCls, btnPrimary, NOW_TYPES } from "@/lib/api";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const NowModal = ({ open, onClose, onSaved, presetType = "availability", existing = null }) => {
  const [type, setType] = useState(presetType);
  const [message, setMessage] = useState("");
  const [startAt, setStartAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setType(existing?.type || presetType);
      setMessage(existing?.message || "");
      setStartAt(toLocalInput(existing?.start_at));
      setExpiresAt(toLocalInput(existing?.expires_at));
    }
  }, [open, existing, presetType]);

  if (!open) return null;

  const save = async () => {
    if (!message.trim()) {
      toast.error("Add a short message.");
      return;
    }
    setBusy(true);
    try {
      const body = JSON.stringify({
        type,
        message: message.trim(),
        start_at: startAt ? new Date(startAt).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      if (existing) {
        await api(`/now/${existing.now_id}`, { method: "PUT", body });
        toast.success("Update saved");
      } else {
        await api("/now", { method: "POST", body });
        toast.success("Update posted");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-5" data-testid="now-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-medium text-guinep-ink">{existing ? "Edit update" : "New update"}</h3>
          <button onClick={onClose} data-testid="now-modal-close" className="text-guinep-mute hover:text-guinep-ink"><X size={18} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className={labelCls}>Update type</label>
            <select data-testid="now-type-select" className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
              {NOW_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Message</label>
            <textarea data-testid="now-message-input" className={`${inputCls} min-h-[80px] resize-none`} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. 2 retwist openings Saturday" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className={labelCls}>Show from</label>
              <input type="datetime-local" data-testid="now-start-input" className={inputCls} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </div>
            <div className="flex-1">
              <label className={labelCls}>Hide after</label>
              <input type="datetime-local" data-testid="now-expires-input" className={inputCls} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-guinep-mute/70">Leave both empty to show the update on your page immediately. Set “Show from” only if you want it to appear at a later date — it stays hidden until then.</p>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 rounded-xl py-3 text-sm font-semibold text-guinep-mute transition-colors hover:text-guinep-ink">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="now-post-button" className="flex-[2] rounded-xl bg-guinep-deep py-3 text-sm font-semibold text-white transition-colors hover:bg-guinep-mid disabled:opacity-50">
              {existing ? "Save" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NowModal;
